import { ref as _ref } from 'vue'
/**
 * Plans Store for Vue 2
 *
 * Manages customer plan data that defines feature availability based on subscription.
 * This is a shared store - all components access the same plan data.
 *
 * @example
 * ```typescript
 * import { usePlans, PlanTypes } from '@/composables/plans';
 *
 * const plans = usePlans();
 *
 * await plans.fetchPlan(PlanTypes.INTEGRATION);
 * console.log(plans.getIntegrationPlan.value);
 *
 * if (plans.getIntegrationPlan.value?.isDefaultRefreshRateEnforced) {
 *   // Feature is limited by plan
 * }
 * ```
 */
import Vue, { computed } from 'vue';
import { PlansService, GetPlanResponseDto } from '@/api';
import {
  type GetPlanDataCatalogResponseDto,
  type GetPlanIntegrationResponseDto,
  type GetPlanMonitoringResponseDto,
  type GetPlanSettingsResponseDto,
  GetPlanTenantResponseDto,
} from '@/api';
import i18n from '@/i18n';

export type PlanType = GetPlanResponseDto.type;
export const PlanTypes = GetPlanResponseDto.type;

type PlanResponseDto =
  | GetPlanDataCatalogResponseDto
  | GetPlanIntegrationResponseDto
  | GetPlanMonitoringResponseDto
  | GetPlanSettingsResponseDto
  | GetPlanTenantResponseDto;

interface PlansState {
  [GetPlanResponseDto.type.DATA_CATALOG]?: GetPlanDataCatalogResponseDto;
  [GetPlanResponseDto.type.INTEGRATION]?: GetPlanIntegrationResponseDto;
  [GetPlanResponseDto.type.MONITORING]?: GetPlanMonitoringResponseDto;
  [GetPlanResponseDto.type.SETTINGS]?: GetPlanSettingsResponseDto;
  [GetPlanResponseDto.type.TENANT]?: GetPlanTenantResponseDto;
  [key: string]: PlanResponseDto | undefined;
}

// Shared state across all components
let plans = _ref<PlansState>({});
const errors = _ref<Map<PlanType, Error>>(new Map());

// Map to store pending promises for in-flight requests
const pendingRequests = new Map<PlanType, Promise<GetPlanResponseDto>>();

// Shared computed getters (created once)
const getDataCatalogPlan = computed(() => plans.value[GetPlanResponseDto.type.DATA_CATALOG]);
const getIntegrationPlan = computed(() => plans.value[GetPlanResponseDto.type.INTEGRATION]);
const getMonitoringPlan = computed(() => plans.value[GetPlanResponseDto.type.MONITORING]);
const getSettingsPlan = computed(() => plans.value[GetPlanResponseDto.type.SETTINGS]);
const getTenantPlan = computed(() => plans.value[GetPlanResponseDto.type.TENANT]);
const isAnyLoading = computed(() => pendingRequests.size > 0);

// Shared helper functions
const isLoading = (type: PlanType) => pendingRequests.has(type);
const getError = (type: PlanType) => errors.value.get(type);
const hasLoaded = (type: PlanType) => plans.value[type] !== undefined || errors.value.has(type);

// Shared action: fetch a plan from the API
async function fetchPlan(type: PlanType) {
  // Return cached plan if already loaded
  if (plans.value[type]) {
    return plans.value[type] as GetPlanResponseDto;
  }

  // Return existing promise if request is already in flight
  if (pendingRequests.has(type)) {
    return pendingRequests.get(type)!;
  }

  // Create new request promise
  const requestPromise = (async () => {
    errors.value.delete(type);

    try {
      const response = await PlansService.getPlanByType({ type });

      switch (type) {
        case GetPlanResponseDto.type.DATA_CATALOG:
          Vue.set(plans.value, type, response as GetPlanDataCatalogResponseDto);
          break;
        case GetPlanResponseDto.type.INTEGRATION:
          Vue.set(plans.value, type, response as GetPlanIntegrationResponseDto);
          break;
        case GetPlanResponseDto.type.MONITORING:
          Vue.set(plans.value, type, response as GetPlanMonitoringResponseDto);
          break;
        case GetPlanResponseDto.type.SETTINGS:
          Vue.set(plans.value, type, response as GetPlanSettingsResponseDto);
          break;
        case GetPlanResponseDto.type.TENANT:
          Vue.set(plans.value, type, response as GetPlanTenantResponseDto);
          break;
        default:
          throw new Error(`Unknown plan type: ${type}`);
      }

      return response as GetPlanResponseDto;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      errors.value.set(type, err);
      throw err;
    } finally {
      // Remove from pending requests when complete (success or error)
      pendingRequests.delete(type);
    }
  })();

  // Store the promise for concurrent requests
  pendingRequests.set(type, requestPromise);

  return requestPromise;
}

// Shared action: fetch multiple plans in parallel
async function fetchMultiplePlans(types: PlanType[]) {
  const results = await Promise.allSettled(types.map((type) => fetchPlan(type)));

  const successfulPlans: GetPlanResponseDto[] = [];
  const fetchErrors: Array<{ type: PlanType; error: Error }> = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      successfulPlans.push(result.value as GetPlanResponseDto);
    } else {
      fetchErrors.push({
        type: types[index],
        error: result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
      });
    }
  });

  return { plans: successfulPlans, errors: fetchErrors };
}

// Shared action: clear all cached plans
function clearPlans() {
  plans.value = {};
  errors.value.clear();
  pendingRequests.clear();
}

// Shared action: clear a specific cached plan
function clearPlan(type: PlanType) {
  Vue.delete(plans.value, type);
  errors.value.delete(type);
  pendingRequests.delete(type);
}

function refreshPlan(type: PlanType): Promise<GetPlanResponseDto> {
  clearPlan(type);
  return fetchPlan(type);
}

function refreshMultiplePlans(types: PlanType[]): void {
  types.forEach((type) => refreshPlan(type));
}

// Shared action: refresh all cached plans
function refreshAllPlans() {
  Object.values(PlanTypes).forEach((type) => refreshPlan(type));
}

/**
 * Get a translated string based on the tenant plan type
 * Appends `_trial` to the i18n key when tenant plan is FREE_TRIAL
 * @param i18nKey - The i18n key to translate
 * @param params - The parameters to pass to the translation
 * @returns The translated string
 */
function getPlanAwareTranslation(i18nKey: string, params?: Record<string, unknown>): string {
  const tenantPlan = plans.value[GetPlanResponseDto.type.TENANT];
  let translationKey = i18nKey;
  const trialTranslationKey = `${i18nKey}_trial`;
  if (tenantPlan?.planType === GetPlanTenantResponseDto.planType.FREE_TRIAL && i18n.t(trialTranslationKey) !== trialTranslationKey) {
    translationKey = trialTranslationKey;
  }
  return i18n.t(translationKey, params);
}

// Export the composable that returns all shared state and functions
export function usePlans() {
  return {
    getTenantPlan,
    getDataCatalogPlan,
    getIntegrationPlan,
    getMonitoringPlan,
    getSettingsPlan,
    isLoading,
    isAnyLoading,
    getError,
    hasLoaded,
    fetchPlan,
    fetchMultiplePlans,
    clearPlans,
    clearPlan,
    plans: plans.value,
    refreshAllPlans,
    refreshMultiplePlans,
    refreshPlan,
    getPlanAwareTranslation,
  };
}
